import { queryOptions, useQuery } from '@tanstack/react-query'
import { fetchCommunicationPreferences } from '@/features/account/services/Preferences/fetchers'
import { getIsSmsOptInPending } from '@/features/account/services/Preferences/utils'
import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns'
import { useRef } from 'react'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'

const PreferencesQuery = 'Preferences'

const POLLING_MAX_DURATION_MS = secondsToMilliseconds(60)
const POLLING_INTERVAL_MS = secondsToMilliseconds(10)

export const communicationPreferencesQueryOptions = (user_id: number | null) => {
  return queryOptions({
    queryKey: [PreferencesQuery, { user_id }] as const,
    queryFn: fetchCommunicationPreferences,
  })
}

export const useQueryCommunicationPreferences = ({ isPollingEnabled = false } = {}) => {
  const { userId } = useSession()
  const pollingStartTimeRef = useRef<number | undefined>(undefined)

  return useQuery({
    ...communicationPreferencesQueryOptions(userId),
    refetchInterval: (query) => {
      const isSmsOptInPending = getIsSmsOptInPending(query.state.data?.communications?.sms)

      // If polling isn't enabled or SMS opt-in isn't pending, reset the polling
      // start time, and do not refetch
      if (!isPollingEnabled || !isSmsOptInPending) {
        pollingStartTimeRef.current = undefined
        return false
      }

      // Set the start time at the first instance when polling conditions are met
      if (pollingStartTimeRef.current === undefined) {
        pollingStartTimeRef.current = Date.now()
      }

      const elapsedTime = Date.now() - pollingStartTimeRef.current

      // Stop polling if the elapsed time exceeds the maximum allowed duration.
      // Then reset the polling start time.
      if (elapsedTime > POLLING_MAX_DURATION_MS) {
        pollingStartTimeRef.current = undefined
        return false
      }

      // Return the polling interval duration
      return POLLING_INTERVAL_MS
    },
    staleTime: minutesToMilliseconds(5),
    enabled: Boolean(userId),
  })
}

export type CommunicationPreferencesKey = readonly [
  typeof PreferencesQuery,
  { user_id: number | null },
]
